/**
 * Starting a workflow run without a conversation (`POST /public/v1/workflow-runs`, contract 5.3):
 * the widget when its panel opens, the web app on an empty chat, and later Home options. The
 * response's messages are shown like normal bot messages (with their card) before the visitor has
 * a conversation; the first reply creates it server-side.
 */
import { z } from "zod";
import { chatActionSchema, decodeActionEvent } from "../decode";
import { articleCardsOf } from "./article";
import { resolveHeaders, type HeadersSource } from "../stream-client";
import type { ChatMessage } from "../types";
import { createMessage } from "../chat-state";
import { WORKFLOW_CAPS, workflowRunRef, type WorkflowRunRef, type WorkflowTrigger } from "./contract";

const runMessage = z.object({
  text: z.string(),
  /** A card that cannot be decoded is dropped; its text still shows. */
  action: chatActionSchema.nullish().catch(undefined),
  /** Article cards shown above the card (P5-6); one that cannot be decoded is dropped. */
  articles: z.array(z.unknown()).nullish().catch(undefined),
});

const runResponse = z.object({
  run: workflowRunRef.nullish().catch(null),
  messages: z.array(runMessage).catch([]),
  conversation_id: z.string().min(1).nullish().catch(null),
});

export interface StartWorkflowRunInput {
  agent_key: string;
  trigger: WorkflowTrigger;
  /** `home_option`: the Home option the visitor picked. */
  option_id?: string;
  /** An open conversation continues instead of starting a new run. */
  conversation_id?: string | null;
  page?: { url?: string; referrer?: string };
}

export interface StartWorkflowRunOptions {
  /** Full URL of `POST /public/v1/workflow-runs`. */
  endpoint: string;
  headers?: HeadersSource;
  /** The client's `caps` (default: the workflow cards); the widget adds the ticket cards. */
  caps?: readonly string[];
  fetchImpl?: typeof fetch;
  signal?: AbortSignal;
}

export interface WorkflowRunStart {
  /** Null when no facing workflow matched: the client shows nothing extra. */
  run: WorkflowRunRef | null;
  /** The run's assistant messages, finished and ready to render. */
  messages: ChatMessage[];
  /** Set when the run needed a conversation right away (a booking card, a person): the client keeps using it. */
  conversationId?: string;
}

const NO_RUN: WorkflowRunStart = { run: null, messages: [] };

export class WorkflowRunError extends Error {
  constructor(readonly status: number) {
    super(`workflow run failed (HTTP ${String(status)})`);
  }
}

/** Stable per run and position, so a re-sent run (debounced `widget_opened`) never duplicates a message. */
export function workflowMessageId(runId: string, index: number): string {
  return `${runId}:${String(index)}`;
}

/** The `workflow` of `GET /public/v1/conversations/{id}`: the conversation's active run, or null (none, absent or malformed). */
export function decodeRestoredWorkflow(raw: unknown): WorkflowRunRef | null {
  const parsed = workflowRunRef.nullish().catch(null).safeParse(raw);
  return parsed.success ? (parsed.data ?? null) : null;
}

/** Decodes a `POST /public/v1/workflow-runs` body; malformed → no run. */
export function decodeWorkflowRun(raw: unknown): WorkflowRunStart {
  const parsed = runResponse.safeParse(raw);
  if (!parsed.success || !parsed.data.run) return NO_RUN;
  const { run, messages, conversation_id: conversationId } = parsed.data;
  return {
    run,
    ...(conversationId ? { conversationId } : {}),
    messages: messages.map((message, index) => {
      const built = { ...createMessage(workflowMessageId(run.run_id, index), "assistant", message.text), workflowRunId: run.run_id };
      const articles = articleCardsOf((message.articles ?? []).map(decodeActionEvent));
      const withCard = message.action ? { ...built, action: message.action } : built;
      return articles.length > 0 ? { ...withCard, articles } : withCard;
    }),
  };
}

/** Starts a facing run. Rejects with `WorkflowRunError` on HTTP errors (hosts renew a 401 token); callers otherwise ignore failures. */
export async function startWorkflowRun(options: StartWorkflowRunOptions, input: StartWorkflowRunInput): Promise<WorkflowRunStart> {
  const headers = resolveHeaders(options.headers);
  headers.set("Accept", "application/json");
  headers.set("Content-Type", "application/json");
  const fetchImpl = options.fetchImpl ?? fetch;
  const body = JSON.stringify({ ...input, conversation_id: input.conversation_id ?? undefined, caps: [...(options.caps ?? WORKFLOW_CAPS)] });
  const response = await fetchImpl(options.endpoint, { method: "POST", headers, body, signal: options.signal });
  if (!response.ok) throw new WorkflowRunError(response.status);
  return decodeWorkflowRun(await response.json().catch(() => null));
}
